import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  User, Camera, Save, LogOut, Plus, X, Linkedin, Github, Youtube, Globe,
  Download, Edit2, ExternalLink, ArrowLeft, Info, GraduationCap, BookOpen,
  UserCheck, Phone, Mail, Briefcase, Trash2, FolderOpen, FileText, Award, Eye,
  ShieldCheck, ShieldX, Clock, Send, Loader2, CheckCircle2, XCircle, History, MessageSquare
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { MessagesPanel } from '../components/MessagesPanel';
import { timeAgo, freshness } from '../lib/timeAgo';
import { isProfessional, getPlanLabel, FREE_SKILL_LIMIT } from '../lib/plan';
import { sortByLatest } from '../lib/sortByLatest';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const portfolioPlatforms = [
  { id: 'github', name: 'GitHub', icon: Github, placeholder: 'https://github.com/username' },
  { id: 'linkedin', name: 'LinkedIn', icon: Linkedin, placeholder: 'https://linkedin.com/in/username' },
  { id: 'youtube', name: 'YouTube', icon: Youtube, placeholder: 'https://youtube.com/@username' },
  { id: 'behance', name: 'Behance', icon: Globe, placeholder: 'https://behance.net/username' },
  { id: 'dribbble', name: 'Dribbble', icon: Globe, placeholder: 'https://dribbble.com/username' },
  { id: 'orcid', name: 'ORCID', icon: Globe, placeholder: 'https://orcid.org/0000-0000-0000-0000' },
  { id: 'google_drive', name: 'Google Drive', icon: Globe, placeholder: 'https://drive.google.com/...' },
  { id: 'portfolio', name: 'Personal Website', icon: Globe, placeholder: 'https://yourwebsite.com' },
];

const emptyEducation = { type: 'tertiary', institution: '', qualification: '', field_of_study: '', start_year: '', end_year: '' };
const emptyExperience = { company: '', role: '', location: '', start_date: '', end_date: '', is_current: false, responsibilities: '' };
const emptySkill = { name: '', reference: { name: '', surname: '', title: '', organisation: '', email: '', phone: '' } };

const CHART_COLORS = ['#01354d', '#c89c3a', '#0e7490', '#059669', '#7c3aed', '#dc2626'];

const folderTabs = [
  { id: 'profile', label: 'My Profile', icon: User, color: 'text-blue-600' },
  { id: 'education', label: 'Education/Training', icon: GraduationCap, color: 'text-emerald-600' },
  { id: 'experience', label: 'Experience', icon: Briefcase, color: 'text-rose-600' },
  { id: 'skills', label: 'Skills & References', icon: Award, color: 'text-amber-600' },
  { id: 'portfolio', label: 'Portfolio', icon: FolderOpen, color: 'text-violet-600' },
  { id: 'messages', label: 'Messages', icon: MessageSquare, color: 'text-pink-600' },
  { id: 'preview', label: 'Preview', icon: Eye, color: 'text-cyan-600' },
];

const genderOptions = ['Male', 'Female', 'Non-Binary', 'Prefer not to say'];

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function ensureAbsoluteUrl(url) {
  if (!url) return url;
  if (url.match(/^https?:\/\//i)) return url;
  return `https://${url}`;
}

export default function CandidateDashboard() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const autosaveTimer = useRef(null);
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [autosaveStatus, setAutosaveStatus] = useState(''); // '', 'saving', 'saved'
  const [successMessage, setSuccessMessage] = useState('');
  const [referenceStatuses, setReferenceStatuses] = useState([]);
  const [verificationLogs, setVerificationLogs] = useState([]);
  const [sendingInvite, setSendingInvite] = useState(null);
  const [profile, setProfile] = useState({
    headline: '', bio: '', country: '', city: '', gender: '',
    skills: [], portfolio_links: {}, profile_picture: null,
    education: [], experience: [],
  });
  const [profileUpdatedAt, setProfileUpdatedAt] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) { navigate('/login'); return; }
    setUser(JSON.parse(storedUser));
    loadProfile();
    loadReferenceStatuses();
    loadVerificationLogs();
  }, [navigate]);

  const loadProfile = async () => {
    try {
      const res = await axios.get(`${API}/profile`, { headers: getAuthHeaders() });
      if (res.data && res.data.exists !== false) {
        let skills = res.data.skills || [];
        // Migrate old string skills to new format
        if (skills.length > 0 && typeof skills[0] === 'string') {
          skills = skills.map(s => ({ name: s, reference: { name: '', surname: '', title: '', organisation: '', email: '', phone: '' } }));
        }
        setProfile({
          headline: res.data.headline || '', bio: res.data.bio || '',
          country: res.data.country || '', city: res.data.city || '',
          gender: res.data.gender || '',
          skills, portfolio_links: res.data.portfolio_links || {},
          profile_picture: res.data.profile_picture || null,
          education: res.data.education || [],
          experience: res.data.experience || [],
        });
        setProfileUpdatedAt(res.data.updated_at || null);
      }
    } catch (err) { console.error('Failed to load profile:', err); }
  };

  const loadReferenceStatuses = async () => {
    try {
      const res = await axios.get(`${API}/references/status`, { headers: getAuthHeaders() });
      setReferenceStatuses(res.data || []);
    } catch (err) { console.error('Failed to load reference statuses:', err); }
  };

  const loadVerificationLogs = async () => {
    try {
      const res = await axios.get(`${API}/verification-logs`, { headers: getAuthHeaders() });
      setVerificationLogs(res.data || []);
    } catch (err) { console.error('Failed to load verification logs:', err); }
  };

  const handleSendInvite = async (skillIndex) => {
    const skill = profile.skills[skillIndex];
    if (!skill?.reference?.email) { alert('Please add a referee email before sending an invitation.'); return; }
    if (!skill?.name) { alert('Please name the skill first.'); return; }
    setSendingInvite(skillIndex);
    try {
      await axios.post(`${API}/references/invite`, {
        skill_index: skillIndex, skill_name: skill.name,
        referee_name: `${skill.reference.name || ''} ${skill.reference.surname || ''}`.trim() || 'Referee',
        referee_email: skill.reference.email,
      }, { headers: getAuthHeaders() });
      setSuccessMessage(`Verification invite sent to ${skill.reference.email}`);
      setTimeout(() => setSuccessMessage(''), 4000);
      loadReferenceStatuses();
      loadVerificationLogs();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to send invitation.');
    } finally { setSendingInvite(null); }
  };

  const getSkillVerificationStatus = (skillName) => {
    // Return status from the LATEST invite for this skill (sorted by sent_at desc)
    const matches = referenceStatuses
      .filter(r => r.skill_name === skillName)
      .sort((a, b) => (b.sent_at || '').localeCompare(a.sent_at || ''));
    return matches[0] ? matches[0].status : null;
  };

  const getLatestInvitation = (skillIndex) => {
    // Most recent invite for this skill_index
    const matches = referenceStatuses
      .filter(r => r.skill_index === skillIndex)
      .sort((a, b) => (b.sent_at || '').localeCompare(a.sent_at || ''));
    return matches[0] || null;
  };

  const canResendInvite = (skillIndex) => {
    const inv = getLatestInvitation(skillIndex);
    if (!inv) return false;
    if (inv.status === 'confirmed') return false;
    if (inv.status === 'expired' || inv.status === 'declined') return true;
    if (!inv.sent_at) return false;
    const daysSince = (Date.now() - new Date(inv.sent_at).getTime()) / (1000 * 60 * 60 * 24);
    return daysSince >= 7;
  };

  const handleResendInvite = async (skillIndex) => {
    setSendingInvite(skillIndex);
    try {
      await axios.post(`${API}/references/resend/${skillIndex}`, {}, { headers: getAuthHeaders() });
      setSuccessMessage('Reference reminder resent successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      await loadReferenceStatuses();
      loadVerificationLogs();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to resend invitation.');
    } finally {
      setSendingInvite(null);
    }
  };

  const handleLogout = () => { localStorage.removeItem('user'); localStorage.removeItem('token'); navigate('/login'); };
  const handlePortfolioChange = (id, val) => setProfile(prev => ({ ...prev, portfolio_links: { ...prev.portfolio_links, [id]: val } }));

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('Image size should be less than 5MB'); return; }
    const reader = new FileReader();
    reader.onloadend = () => { setProfile(prev => ({ ...prev, profile_picture: reader.result })); };
    reader.readAsDataURL(file);
  };

  // Education
  const handleAddEducation = () => setProfile(prev => ({ ...prev, education: [...prev.education, { ...emptyEducation }] }));
  const handleUpdateEducation = (i, f, v) => setProfile(prev => { const u = [...prev.education]; u[i] = { ...u[i], [f]: v }; return { ...prev, education: u }; });
  const handleRemoveEducation = (i) => setProfile(prev => ({ ...prev, education: prev.education.filter((_, idx) => idx !== i) }));

  // Experience
  const handleAddExperience = () => setProfile(prev => ({ ...prev, experience: [...prev.experience, { ...emptyExperience }] }));
  const handleUpdateExperience = (i, f, v) => setProfile(prev => { const u = [...prev.experience]; u[i] = { ...u[i], [f]: v }; return { ...prev, experience: u }; });
  const handleRemoveExperience = (i) => setProfile(prev => ({ ...prev, experience: prev.experience.filter((_, idx) => idx !== i) }));

  // Skills with references
  const handleAddSkill = () => {
    if (!isProfessional(user) && profile.skills.length >= FREE_SKILL_LIMIT) {
      alert(`Free plan is limited to ${FREE_SKILL_LIMIT} skills. Upgrade to Professional for unlimited skills with reference verification.`);
      navigate('/pricing');
      return;
    }
    setProfile(prev => ({ ...prev, skills: [...prev.skills, JSON.parse(JSON.stringify(emptySkill))] }));
  };
  const handleUpdateSkillName = (i, val) => setProfile(prev => { const u = [...prev.skills]; u[i] = { ...u[i], name: val }; return { ...prev, skills: u }; });
  const handleUpdateSkillRef = (i, f, val) => setProfile(prev => { const u = [...prev.skills]; u[i] = { ...u[i], reference: { ...u[i].reference, [f]: val } }; return { ...prev, skills: u }; });
  const handleRemoveSkill = (i) => setProfile(prev => ({ ...prev, skills: prev.skills.filter((_, idx) => idx !== i) }));

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      const r = await axios.put(`${API}/profile`, profile, { headers: getAuthHeaders() });
      setSuccessMessage('Profile saved successfully!');
      setIsEditing(false);
      setProfileUpdatedAt(r.data?.updated_at || new Date().toISOString());
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) { alert('Failed to save profile.'); } finally { setIsSaving(false); }
  };

  // Autosave: debounce 2s after last change, only while in edit mode
  useEffect(() => {
    if (!isEditing || !user) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(async () => {
      try {
        setAutosaveStatus('saving');
        const r = await axios.put(`${API}/profile`, profile, { headers: getAuthHeaders() });
        setProfileUpdatedAt(r.data?.updated_at || new Date().toISOString());
        setAutosaveStatus('saved');
        setTimeout(() => setAutosaveStatus(''), 2000);
      } catch (err) {
        setAutosaveStatus('');
      }
    }, 2000);
    return () => { if (autosaveTimer.current) clearTimeout(autosaveTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, isEditing]);

  const handleDownloadPDF = () => {
    const sortedEdu = sortByLatest(profile.education, 'end_year', 'start_year');
    const sortedExp = sortByLatest(profile.experience, 'end_date', 'start_date');
    const eduHtml = sortedEdu.length > 0 ? `<div class="section"><div class="st">Education / Training</div>${sortedEdu.map(e => `<div class="card" style="border-left-color:${e.type === 'tertiary' ? '#01354d' : '#c89c3a'}"><strong>${e.institution || ''}</strong>${e.qualification ? `<br>${e.qualification}${e.field_of_study ? ` in ${e.field_of_study}` : ''}` : ''}${e.start_year ? `<br><small>${e.start_year}${e.end_year ? ` - ${e.end_year}` : ''}</small>` : ''}</div>`).join('')}</div>` : '';
    const expHtml = sortedExp.length > 0 ? `<div class="section"><div class="st">Experience</div>${sortedExp.map(e => `<div class="card" style="border-left-color:#dc2626"><strong>${e.role || ''}</strong>${e.company ? `<br>${e.company}` : ''}${e.location ? ` - ${e.location}` : ''}${e.start_date ? `<br><small>${e.start_date}${e.is_current ? ' - Present' : (e.end_date ? ` - ${e.end_date}` : '')}</small>` : ''}${e.responsibilities ? `<br><small>${e.responsibilities}</small>` : ''}</div>`).join('')}</div>` : '';
    const skillsHtml = profile.skills.length > 0 ? `<div class="section"><div class="st">Skills & References</div>${profile.skills.map(s => `<div class="card" style="border-left-color:#c89c3a"><strong>${s.name}</strong>${s.reference?.name ? `<br><small>Reference: ${s.reference.name} ${s.reference.surname || ''} - ${s.reference.title || ''} at ${s.reference.organisation || ''}</small>` : ''}${s.reference?.email ? `<br><small>Email: ${s.reference.email}${s.reference.phone ? ` | Phone: ${s.reference.phone}` : ''}</small>` : ''}</div>`).join('')}</div>` : '';
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${user?.name} - MeritumPraxis</title><style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Segoe UI',sans-serif;padding:40px;max-width:800px;margin:0 auto;color:#062240}.dlb{position:fixed;top:0;left:0;right:0;background:#01354d;color:white;padding:12px 20px;display:flex;justify-content:space-between;align-items:center;z-index:1000}.dlbtn{background:#c89c3a;color:#01354d;border:none;padding:8px 20px;border-radius:4px;cursor:pointer;font-weight:bold}.ct{margin-top:60px}.hd{text-align:center;margin-bottom:30px;padding-bottom:20px;border-bottom:3px solid #c89c3a}.pp{width:120px;height:120px;border-radius:50%;margin:0 auto 20px;display:block;object-fit:cover;border:3px solid #c89c3a}.nm{font-size:28px;font-weight:bold;color:#01354d;text-align:center}.hl{font-size:16px;color:#c89c3a;text-align:center;margin-top:5px}.loc{font-size:14px;color:#666;text-align:center;margin-top:5px}.section{margin-bottom:25px}.st{font-size:18px;font-weight:bold;color:#01354d;margin-bottom:10px;padding-bottom:5px;border-bottom:2px solid #01354d}.card{margin-bottom:10px;padding:10px;background:#f8f9fa;border-left:3px solid #01354d}.ft{margin-top:40px;padding-top:20px;border-top:2px solid #ddd;text-align:center;font-size:12px;color:#666}@media print{.dlb{display:none!important}.ct{margin-top:0}body{padding:20px}}</style></head><body><div class="dlb"><span>${user?.name} - MeritumPraxis</span><button class="dlbtn" onclick="window.print()">Download PDF</button></div><div class="ct"><div class="hd"><div style="font-size:24px;font-weight:bold;color:#01354d">MeritumPraxis</div><div style="font-size:12px;color:#666">Progress Made Visible</div></div>${profile.profile_picture ? `<img src="${profile.profile_picture}" class="pp">` : ''}<div class="nm">${user?.name || ''}</div>${profile.headline ? `<div class="hl">${profile.headline}</div>` : ''}${(profile.city || profile.country) ? `<div class="loc">${profile.city}${profile.city && profile.country ? ', ' : ''}${profile.country}</div>` : ''}<div class="section"><div class="st">Contact</div><p><strong>Email:</strong> ${user?.email || ''}</p>${profile.gender ? `<p><strong>Gender:</strong> ${profile.gender}</p>` : ''}</div>${profile.bio ? `<div class="section"><div class="st">About</div><p style="line-height:1.6">${profile.bio}</p></div>` : ''}${eduHtml}${expHtml}${skillsHtml}${Object.entries(profile.portfolio_links).filter(([,v]) => v).length > 0 ? `<div class="section"><div class="st">Portfolio</div>${Object.entries(profile.portfolio_links).filter(([,v]) => v).map(([k,v]) => { const p = portfolioPlatforms.find(pl => pl.id === k); return `<div class="card" style="border-left-color:#0e7490"><strong>${p?.name || k}:</strong> ${v}</div>`; }).join('')}</div>` : ''}<div class="ft"><p>Generated by MeritumPraxis - Progress Made Visible</p></div></div></body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); }
  };

  // Profile completion data for pie chart
  const getCompletionData = () => {
    const sections = [
      { name: 'Basic Info', done: !!(profile.headline && profile.country) },
      { name: 'Bio', done: !!profile.bio },
      { name: 'Photo', done: !!profile.profile_picture },
      { name: 'Education', done: profile.education.length > 0 },
      { name: 'Experience', done: profile.experience.length > 0 },
      { name: 'Skills', done: profile.skills.length > 0 },
      { name: 'Portfolio', done: Object.values(profile.portfolio_links).some(Boolean) },
    ];
    const done = sections.filter(s => s.done).length;
    return { sections, done, total: sections.length, percent: Math.round((done / sections.length) * 100) };
  };

  if (!user) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><p>Loading...</p></div>;
  const isPro = isProfessional(user);
  const planLabel = getPlanLabel(user);
  const completion = getCompletionData();
  const pieData = [
    { name: 'Complete', value: completion.done },
    { name: 'Remaining', value: completion.total - completion.done },
  ];

  return (
    <div data-testid="candidate-dashboard" className="min-h-screen bg-slate-100 pt-24">
      {/* Top Bar */}
      <div className="bg-meritum-navy text-white py-4 sm:py-5">
        <div className="container mx-auto px-3 sm:px-4 md:px-6 lg:px-8 max-w-7xl flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Link to="/" className="text-slate-300 hover:text-white flex-shrink-0"><ArrowLeft size={20} /></Link>
            <FileText size={22} className="text-meritum-gold flex-shrink-0 hidden sm:block" />
            <div className="min-w-0">
              <h1 className="font-heading text-base sm:text-xl md:text-2xl font-bold truncate">{user.name}</h1>
              <p className="font-body text-slate-300 text-[10px] sm:text-xs hidden sm:block">Digital Professional File</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
            <Button onClick={handleDownloadPDF} variant="outline" size="sm" className="border-meritum-gold text-meritum-gold hover:bg-meritum-gold hover:text-meritum-navy font-body px-2 sm:px-3" data-testid="download-pdf-btn">
              <Download size={14} className="sm:mr-1" /><span className="hidden sm:inline">PDF</span>
            </Button>
            {isEditing ? (
              <>
                <Button onClick={handleSaveProfile} disabled={isSaving} size="sm" className="bg-meritum-gold text-meritum-navy hover:bg-meritum-gold/90 font-body font-bold px-2 sm:px-3" data-testid="save-profile-btn">
                  <Save size={14} className="sm:mr-1" /><span className="hidden sm:inline">{isSaving ? 'Saving...' : 'Save'}</span>
                </Button>
                <Button onClick={() => setIsEditing(false)} variant="outline" size="sm" className="border-white/30 text-white hover:bg-white/10 font-body px-2 sm:px-3 hidden sm:inline-flex">Cancel</Button>
              </>
            ) : (
              <Button onClick={() => setIsEditing(true)} variant="outline" size="sm" className="border-white text-white hover:bg-white hover:text-meritum-navy font-body px-2 sm:px-3" data-testid="edit-profile-btn">
                <Edit2 size={14} className="mr-1" />Edit
              </Button>
            )}
            <Button onClick={handleLogout} variant="outline" size="sm" className="border-red-400/50 text-red-300 hover:bg-red-500 hover:text-white hover:border-red-500 font-body px-2 sm:px-3" data-testid="logout-btn">
              <LogOut size={14} className="sm:mr-1" /><span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </div>

      {successMessage && (
        <div className="bg-green-100 border-b border-green-200 py-2">
          <div className="container mx-auto px-4 max-w-7xl"><p className="text-green-700 font-body text-sm" data-testid="success-message">{successMessage}</p></div>
        </div>
      )}

      {/* Free plan upgrade banner */}
      {!isPro && (
        <div className="bg-gradient-to-r from-meritum-gold/15 via-amber-50 to-meritum-gold/10 border-b border-meritum-gold/30 py-3" data-testid="free-upgrade-banner">
          <div className="container mx-auto px-4 max-w-7xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
            <p className="font-body text-xs sm:text-sm text-meritum-navy">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-200 text-slate-600 rounded text-[10px] font-bold uppercase tracking-wider mr-2">{planLabel}</span>
              You&apos;re on the <strong>Free plan</strong>. Unlock unlimited skills and on-platform messaging with organizations.
            </p>
            <Button onClick={() => navigate('/pricing')} size="sm" className="bg-meritum-gold text-meritum-navy hover:bg-meritum-gold/90 font-body font-bold flex-shrink-0" data-testid="upgrade-cta-btn">
              <Award size={14} className="mr-1" />Upgrade to Professional
            </Button>
          </div>
        </div>
      )}

      {/* Folder Tabs */}
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="flex items-end gap-1 pt-6 overflow-x-auto" data-testid="folder-tabs">
          {folderTabs.map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`folder-tab-${tab.id}`}
              className={`flex items-center gap-2 px-5 py-3 rounded-t-lg font-body text-sm font-medium transition-all duration-200 border border-b-0 whitespace-nowrap ${
                activeTab === tab.id ? 'bg-white text-meritum-navy border-slate-200 shadow-sm -mb-px z-10' : 'bg-slate-200/70 text-slate-500 hover:bg-slate-200 hover:text-meritum-navy border-transparent'
              }`}>
              <tab.icon size={16} className={activeTab === tab.id ? tab.color : ''} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="border-t border-slate-200">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl py-8">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-4">
              <div className="bg-white rounded-xl shadow-sm p-5 border border-slate-100">
                <div className="text-center">
                  <div className="relative inline-block mb-4">
                    <div className="w-28 h-28 rounded-full bg-meritum-teal/10 flex items-center justify-center mx-auto overflow-hidden border-4 border-meritum-gold/30">
                      {profile.profile_picture ? <img src={profile.profile_picture} alt="Profile" className="w-full h-full object-cover" /> : <User size={40} className="text-meritum-teal" />}
                    </div>
                    {isEditing && (
                      <>
                        <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/*" className="hidden" data-testid="profile-image-input" />
                        <button onClick={() => fileInputRef.current?.click()} className="absolute bottom-0 right-0 w-8 h-8 bg-meritum-gold rounded-full flex items-center justify-center shadow-lg" data-testid="upload-image-btn"><Camera size={14} className="text-meritum-navy" /></button>
                      </>
                    )}
                  </div>
                  <h2 className="font-heading text-lg font-semibold text-meritum-navy">{user.name}</h2>
                  <p className="font-body text-xs text-slate-500 mt-1">{user.email}</p>
                  {profile.headline && <p className="font-body text-sm text-meritum-teal font-medium mt-2">{profile.headline}</p>}
                  {(profile.city || profile.country) && <p className="font-body text-xs text-slate-400 mt-1">{profile.city}{profile.city && profile.country ? ', ' : ''}{profile.country}</p>}
                  {profileUpdatedAt && (
                    <p
                      className={`font-body text-[10px] mt-3 inline-flex items-center gap-1 px-2 py-1 rounded-full ${
                        freshness(profileUpdatedAt) === 'fresh' ? 'bg-emerald-50 text-emerald-700' :
                        freshness(profileUpdatedAt) === 'stale' ? 'bg-amber-50 text-amber-700' :
                        'bg-slate-100 text-slate-500'
                      }`}
                      data-testid="profile-last-updated"
                    >
                      <Clock size={10} />Last updated {timeAgo(profileUpdatedAt)}
                    </p>
                  )}
                </div>
              </div>

              {/* Profile Completion Chart */}
              <div className="bg-white rounded-xl shadow-sm p-5 border border-slate-100">
                <h4 className="font-heading text-sm font-semibold text-meritum-navy mb-3">Profile Completion</h4>
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={pieData} dataKey="value" cx="50%" cy="50%" innerRadius={22} outerRadius={35} strokeWidth={0}>
                          <Cell fill="#059669" />
                          <Cell fill="#e2e8f0" />
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div>
                    <p className="font-heading text-2xl font-bold text-meritum-navy">{completion.percent}%</p>
                    <p className="font-body text-xs text-slate-500">{completion.done}/{completion.total} sections</p>
                  </div>
                </div>
                <div className="mt-3 space-y-1.5">
                  {completion.sections.map((s, i) => (
                    <div key={i} className="flex items-center gap-2 font-body text-xs">
                      <div className={`w-2 h-2 rounded-full ${s.done ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                      <span className={s.done ? 'text-slate-700' : 'text-slate-400'}>{s.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Skills Distribution Chart */}
              {profile.skills.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm p-5 border border-slate-100">
                  <h4 className="font-heading text-sm font-semibold text-meritum-navy mb-3">Skills Overview</h4>
                  <div className="w-full h-32">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={profile.skills.map((s, i) => ({ name: s.name || `Skill ${i+1}`, value: 1 }))} dataKey="value" cx="50%" cy="50%" outerRadius={50} strokeWidth={1} stroke="#fff">
                          {profile.skills.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={(_, name) => [name, '']} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {profile.skills.slice(0, 6).map((s, i) => (
                      <span key={i} className="flex items-center gap-1 text-[10px] font-body text-slate-600">
                        <span className="w-2 h-2 rounded-full inline-block" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                        {s.name || '...'}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Content */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-100 min-h-[400px]">

                {/* Profile Tab */}
                {activeTab === 'profile' && (
                  <div className="space-y-5" data-testid="tab-content-profile">
                    <h3 className="font-heading text-lg font-semibold text-meritum-navy flex items-center gap-2"><User size={20} className="text-blue-600" />Basic Information</h3>
                    <div className="space-y-4">
                      <div>
                        <Label className="font-body text-sm font-medium text-meritum-navy">Professional Headline</Label>
                        {isEditing ? <Input value={profile.headline} onChange={(e) => setProfile(p => ({ ...p, headline: e.target.value }))} placeholder="e.g., Software Developer | Data Analyst" className="mt-1" data-testid="headline-input" /> : <p className="font-body text-slate-600 mt-1">{profile.headline || 'No headline set'}</p>}
                      </div>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <Label className="font-body text-sm font-medium text-meritum-navy">Country</Label>
                          {isEditing ? <Input value={profile.country} onChange={(e) => setProfile(p => ({ ...p, country: e.target.value }))} placeholder="e.g., South Africa" className="mt-1" data-testid="country-input" /> : <p className="font-body text-slate-600 mt-1">{profile.country || 'Not specified'}</p>}
                        </div>
                        <div>
                          <Label className="font-body text-sm font-medium text-meritum-navy">City</Label>
                          {isEditing ? <Input value={profile.city} onChange={(e) => setProfile(p => ({ ...p, city: e.target.value }))} placeholder="e.g., Cape Town" className="mt-1" data-testid="city-input" /> : <p className="font-body text-slate-600 mt-1">{profile.city || 'Not specified'}</p>}
                        </div>
                        <div>
                          <Label className="font-body text-sm font-medium text-meritum-navy">Gender</Label>
                          {isEditing ? (
                            <Select value={profile.gender} onValueChange={(v) => setProfile(p => ({ ...p, gender: v }))}>
                              <SelectTrigger className="mt-1" data-testid="gender-select"><SelectValue placeholder="Select gender" /></SelectTrigger>
                              <SelectContent>
                                {genderOptions.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          ) : <p className="font-body text-slate-600 mt-1">{profile.gender || 'Not specified'}</p>}
                        </div>
                      </div>
                      <div>
                        <Label className="font-body text-sm font-medium text-meritum-navy">Bio</Label>
                        {isEditing ? <Textarea value={profile.bio} onChange={(e) => setProfile(p => ({ ...p, bio: e.target.value }))} placeholder="Tell us about yourself..." rows={4} className="mt-1 resize-none" data-testid="bio-input" /> : <p className="font-body text-slate-600 mt-1 whitespace-pre-wrap">{profile.bio || 'No bio added yet'}</p>}
                      </div>
                    </div>
                  </div>
                )}

                {/* Education/Training Tab */}
                {activeTab === 'education' && (
                  <div className="space-y-5" data-testid="tab-content-education">
                    <div className="flex items-center justify-between">
                      <h3 className="font-heading text-lg font-semibold text-meritum-navy flex items-center gap-2"><GraduationCap size={20} className="text-emerald-600" />Education / Training</h3>
                      {isEditing && <Button onClick={handleAddEducation} size="sm" className="bg-meritum-gold text-meritum-navy hover:bg-meritum-gold/90 font-body" data-testid="add-education-btn"><Plus size={14} className="mr-1" />Add</Button>}
                    </div>
                    <div className="flex items-start gap-2 p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                      <Info size={14} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                      <p className="font-body text-xs text-slate-600">Add your tertiary (university, college) and high school education or training.</p>
                    </div>
                    {profile.education.length > 0 ? (
                      <div className="space-y-4">
                        {(isEditing ? profile.education : sortByLatest(profile.education, 'end_year', 'start_year')).map((edu, index) => (
                          <div key={index} className={`border rounded-lg p-4 ${edu.type === 'tertiary' ? 'border-emerald-200 bg-emerald-50/50' : 'border-amber-200 bg-amber-50/50'}`} data-testid={`education-entry-${index}`}>
                            {isEditing ? (
                              <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                  <Select value={edu.type} onValueChange={(v) => handleUpdateEducation(index, 'type', v)}>
                                    <SelectTrigger className="w-48" data-testid={`edu-type-select-${index}`}><SelectValue /></SelectTrigger>
                                    <SelectContent><SelectItem value="tertiary">Tertiary (University/College)</SelectItem><SelectItem value="highschool">High School</SelectItem></SelectContent>
                                  </Select>
                                  <Button onClick={() => handleRemoveEducation(index)} variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`remove-edu-btn-${index}`}><Trash2 size={16} /></Button>
                                </div>
                                <div><Label className="font-body text-sm text-meritum-navy">Institution</Label><Input value={edu.institution} onChange={(e) => handleUpdateEducation(index, 'institution', e.target.value)} placeholder={edu.type === 'tertiary' ? 'e.g., University of Cape Town' : 'e.g., Pretoria High School'} className="mt-1" data-testid={`edu-institution-${index}`} /></div>
                                <div className="grid md:grid-cols-2 gap-3">
                                  <div><Label className="font-body text-sm text-meritum-navy">Qualification</Label><Input value={edu.qualification} onChange={(e) => handleUpdateEducation(index, 'qualification', e.target.value)} placeholder={edu.type === 'tertiary' ? 'e.g., BSc, Masters' : 'e.g., Matric'} className="mt-1" data-testid={`edu-qualification-${index}`} /></div>
                                  <div><Label className="font-body text-sm text-meritum-navy">Field of Study</Label><Input value={edu.field_of_study} onChange={(e) => handleUpdateEducation(index, 'field_of_study', e.target.value)} placeholder="e.g., Computer Science" className="mt-1" data-testid={`edu-field-${index}`} /></div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                  <div><Label className="font-body text-sm text-meritum-navy">Start Year</Label><Input value={edu.start_year} onChange={(e) => handleUpdateEducation(index, 'start_year', e.target.value)} placeholder="e.g., 2020" className="mt-1" data-testid={`edu-start-${index}`} /></div>
                                  <div><Label className="font-body text-sm text-meritum-navy">End Year</Label><Input value={edu.end_year} onChange={(e) => handleUpdateEducation(index, 'end_year', e.target.value)} placeholder="e.g., 2024" className="mt-1" data-testid={`edu-end-${index}`} /></div>
                                </div>
                              </div>
                            ) : (
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  {edu.type === 'tertiary' ? <GraduationCap size={14} className="text-emerald-600" /> : <BookOpen size={14} className="text-amber-600" />}
                                  <span className="font-body text-[10px] font-medium uppercase tracking-wide text-slate-400">{edu.type === 'tertiary' ? 'Tertiary' : 'High School'}</span>
                                </div>
                                <h4 className="font-heading font-semibold text-meritum-navy">{edu.institution || 'Institution not specified'}</h4>
                                {(edu.qualification || edu.field_of_study) && <p className="font-body text-sm text-slate-600">{edu.qualification}{edu.qualification && edu.field_of_study ? ' in ' : ''}{edu.field_of_study}</p>}
                                {(edu.start_year || edu.end_year) && <p className="font-body text-xs text-slate-400 mt-1">{edu.start_year}{edu.start_year && edu.end_year ? ' - ' : ''}{edu.end_year}</p>}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : <p className="font-body text-slate-400 text-sm">No education added yet.</p>}
                  </div>
                )}

                {/* Experience Tab */}
                {activeTab === 'experience' && (
                  <div className="space-y-5" data-testid="tab-content-experience">
                    <div className="flex items-center justify-between">
                      <h3 className="font-heading text-lg font-semibold text-meritum-navy flex items-center gap-2"><Briefcase size={20} className="text-rose-600" />Work Experience</h3>
                      {isEditing && <Button onClick={handleAddExperience} size="sm" className="bg-meritum-gold text-meritum-navy hover:bg-meritum-gold/90 font-body" data-testid="add-experience-btn"><Plus size={14} className="mr-1" />Add</Button>}
                    </div>
                    <div className="flex items-start gap-2 p-3 bg-rose-50 rounded-lg border border-rose-100">
                      <Info size={14} className="text-rose-600 mt-0.5 flex-shrink-0" />
                      <p className="font-body text-xs text-slate-600">Add your work experience including companies, roles, dates, and key responsibilities.</p>
                    </div>
                    {profile.experience.length > 0 ? (
                      <div className="space-y-4">
                        {(isEditing ? profile.experience : sortByLatest(profile.experience, 'end_date', 'start_date')).map((exp, index) => (
                          <div key={index} className="border border-rose-200 rounded-lg p-4 bg-rose-50/30" data-testid={`experience-entry-${index}`}>
                            {isEditing ? (
                              <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                  <Label className="font-heading text-sm font-semibold text-meritum-navy">Position {index + 1}</Label>
                                  <Button onClick={() => handleRemoveExperience(index)} variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`remove-exp-btn-${index}`}><Trash2 size={16} /></Button>
                                </div>
                                <div className="grid md:grid-cols-2 gap-3">
                                  <div><Label className="font-body text-sm text-meritum-navy">Job Title / Role</Label><Input value={exp.role} onChange={(e) => handleUpdateExperience(index, 'role', e.target.value)} placeholder="e.g., Software Developer" className="mt-1" data-testid={`exp-role-${index}`} /></div>
                                  <div><Label className="font-body text-sm text-meritum-navy">Company</Label><Input value={exp.company} onChange={(e) => handleUpdateExperience(index, 'company', e.target.value)} placeholder="e.g., Tech Corp" className="mt-1" data-testid={`exp-company-${index}`} /></div>
                                </div>
                                <div><Label className="font-body text-sm text-meritum-navy">Location</Label><Input value={exp.location} onChange={(e) => handleUpdateExperience(index, 'location', e.target.value)} placeholder="e.g., Cape Town, South Africa" className="mt-1" data-testid={`exp-location-${index}`} /></div>
                                <div className="grid grid-cols-2 gap-3">
                                  <div><Label className="font-body text-sm text-meritum-navy">Start Date</Label><Input type="month" value={exp.start_date} onChange={(e) => handleUpdateExperience(index, 'start_date', e.target.value)} className="mt-1" data-testid={`exp-start-${index}`} /></div>
                                  <div>
                                    <Label className="font-body text-sm text-meritum-navy">End Date</Label>
                                    {exp.is_current ? (
                                      <p className="mt-1 text-sm text-meritum-teal font-medium py-2">Present</p>
                                    ) : (
                                      <Input type="month" value={exp.end_date} onChange={(e) => handleUpdateExperience(index, 'end_date', e.target.value)} className="mt-1" data-testid={`exp-end-${index}`} />
                                    )}
                                  </div>
                                </div>
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input type="checkbox" checked={exp.is_current} onChange={(e) => handleUpdateExperience(index, 'is_current', e.target.checked)} className="rounded border-slate-300" data-testid={`exp-current-${index}`} />
                                  <span className="font-body text-sm text-slate-600">I currently work here</span>
                                </label>
                                <div><Label className="font-body text-sm text-meritum-navy">Key Responsibilities</Label><Textarea value={exp.responsibilities} onChange={(e) => handleUpdateExperience(index, 'responsibilities', e.target.value)} placeholder="Describe your main responsibilities and achievements..." rows={3} className="mt-1 resize-none" data-testid={`exp-responsibilities-${index}`} /></div>
                              </div>
                            ) : (
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <Briefcase size={14} className="text-rose-600" />
                                  <span className="font-body text-[10px] font-medium uppercase tracking-wide text-slate-400">{exp.is_current ? 'Current Position' : 'Previous Position'}</span>
                                </div>
                                <h4 className="font-heading font-semibold text-meritum-navy">{exp.role || 'Role not specified'}</h4>
                                {exp.company && <p className="font-body text-sm text-meritum-teal font-medium">{exp.company}</p>}
                                {exp.location && <p className="font-body text-xs text-slate-400">{exp.location}</p>}
                                {exp.start_date && <p className="font-body text-xs text-slate-400 mt-1">{exp.start_date}{exp.is_current ? ' - Present' : (exp.end_date ? ` - ${exp.end_date}` : '')}</p>}
                                {exp.responsibilities && <p className="font-body text-sm text-slate-600 mt-2 whitespace-pre-wrap">{exp.responsibilities}</p>}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : <p className="font-body text-slate-400 text-sm">No experience added yet.</p>}
                  </div>
                )}

                {/* Skills & References Tab */}
                {activeTab === 'skills' && (
                  <div className="space-y-5" data-testid="tab-content-skills">
                    <div className="flex items-center justify-between">
                      <h3 className="font-heading text-lg font-semibold text-meritum-navy flex items-center gap-2"><Award size={20} className="text-amber-600" />Skills & References</h3>
                      {isEditing && <Button onClick={handleAddSkill} size="sm" className="bg-meritum-gold text-meritum-navy hover:bg-meritum-gold/90 font-body" data-testid="add-skill-btn"><Plus size={14} className="mr-1" />Add Skill</Button>}
                    </div>
                    <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-100">
                      <Info size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
                      <p className="font-body text-xs text-slate-600">Each skill requires a reference (supervisor/verifier) who can confirm your competence. Add their name, title, organisation and contact details.</p>
                    </div>
                    {profile.skills.length > 0 ? (
                      <div className="space-y-4">
                        {profile.skills.map((skill, index) => (
                          <div key={index} className="border border-amber-200 rounded-lg overflow-hidden" data-testid={`skill-entry-${index}`}>
                            {isEditing ? (
                              <div className="p-4 space-y-3">
                                <div className="flex items-center justify-between">
                                  <Label className="font-heading text-sm font-semibold text-meritum-navy">Skill {index + 1}</Label>
                                  <Button onClick={() => handleRemoveSkill(index)} variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`remove-skill-btn-${index}`}><Trash2 size={16} /></Button>
                                </div>
                                <div>
                                  <Label className="font-body text-sm text-meritum-navy">Skill Name</Label>
                                  <Input value={skill.name} onChange={(e) => handleUpdateSkillName(index, e.target.value)} placeholder="e.g., Python, Project Management, Data Analysis" className="mt-1" data-testid={`skill-name-${index}`} />
                                </div>
                                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                                  <p className="font-body text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-1"><UserCheck size={12} className="text-amber-600" />Reference / Verifier</p>
                                  <div className="grid md:grid-cols-2 gap-3">
                                    <div><Label className="font-body text-xs text-slate-600">Name</Label><Input value={skill.reference?.name || ''} onChange={(e) => handleUpdateSkillRef(index, 'name', e.target.value)} placeholder="First name" className="mt-1 text-sm" data-testid={`skill-ref-name-${index}`} /></div>
                                    <div><Label className="font-body text-xs text-slate-600">Surname</Label><Input value={skill.reference?.surname || ''} onChange={(e) => handleUpdateSkillRef(index, 'surname', e.target.value)} placeholder="Last name" className="mt-1 text-sm" data-testid={`skill-ref-surname-${index}`} /></div>
                                    <div><Label className="font-body text-xs text-slate-600">Title / Position</Label><Input value={skill.reference?.title || ''} onChange={(e) => handleUpdateSkillRef(index, 'title', e.target.value)} placeholder="e.g., Senior Manager" className="mt-1 text-sm" data-testid={`skill-ref-title-${index}`} /></div>
                                    <div><Label className="font-body text-xs text-slate-600">Organisation</Label><Input value={skill.reference?.organisation || ''} onChange={(e) => handleUpdateSkillRef(index, 'organisation', e.target.value)} placeholder="Company name" className="mt-1 text-sm" data-testid={`skill-ref-org-${index}`} /></div>
                                    <div><Label className="font-body text-xs text-slate-600">Email</Label><Input value={skill.reference?.email || ''} onChange={(e) => handleUpdateSkillRef(index, 'email', e.target.value)} placeholder="email@example.com" className="mt-1 text-sm" data-testid={`skill-ref-email-${index}`} /></div>
                                    <div><Label className="font-body text-xs text-slate-600">Phone</Label><Input value={skill.reference?.phone || ''} onChange={(e) => handleUpdateSkillRef(index, 'phone', e.target.value)} placeholder="+27 12 345 6789" className="mt-1 text-sm" data-testid={`skill-ref-phone-${index}`} /></div>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="p-4">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="w-2 h-2 rounded-full" style={{ background: CHART_COLORS[index % CHART_COLORS.length] }} />
                                  <h4 className="font-heading font-semibold text-meritum-navy">{skill.name || 'Unnamed skill'}</h4>
                                  {(() => {
                                    const status = getSkillVerificationStatus(skill.name);
                                    if (status === 'confirmed') return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full font-body" data-testid={`verified-badge-${index}`}><ShieldCheck size={12} />Verified</span>;
                                    if (status === 'pending') return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full font-body" data-testid={`pending-badge-${index}`}><Clock size={12} />Pending</span>;
                                    if (status === 'declined') return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full font-body" data-testid={`declined-badge-${index}`}><ShieldX size={12} />Declined</span>;
                                    if (status === 'expired') return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full font-body" data-testid={`expired-badge-${index}`}><Clock size={12} />Expired</span>;
                                    return null;
                                  })()}
                                </div>
                                {skill.reference?.name && (
                                  <div className="ml-4 p-3 bg-slate-50 rounded-lg border border-slate-100">
                                    <p className="font-body text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Reference</p>
                                    <p className="font-body text-sm text-meritum-navy font-medium">{skill.reference.name} {skill.reference.surname}</p>
                                    {skill.reference.title && <p className="font-body text-sm text-meritum-teal">{skill.reference.title}{skill.reference.organisation ? ` at ${skill.reference.organisation}` : ''}</p>}
                                    <div className="flex flex-wrap gap-3 mt-1">
                                      {skill.reference.email && <span className="font-body text-xs text-slate-500 flex items-center gap-1"><Mail size={10} />{skill.reference.email}</span>}
                                      {skill.reference.phone && <span className="font-body text-xs text-slate-500 flex items-center gap-1"><Phone size={10} />{skill.reference.phone}</span>}
                                    </div>
                                    {/* Send Invite Button */}
                                    {skill.reference.email && !getSkillVerificationStatus(skill.name) && (
                                      <Button onClick={() => handleSendInvite(index)} disabled={sendingInvite === index} size="sm" className="mt-3 bg-meritum-teal text-white hover:bg-meritum-navy font-body text-xs" data-testid={`send-invite-btn-${index}`}>
                                        {sendingInvite === index ? <><Loader2 size={12} className="mr-1 animate-spin" />Sending...</> : <><Send size={12} className="mr-1" />Send Verification Invite</>}
                                      </Button>
                                    )}
                                    {/* Resend Invite Button (after 7 days, or if declined/expired) */}
                                    {skill.reference.email && canResendInvite(index) && (
                                      <Button onClick={() => handleResendInvite(index)} disabled={sendingInvite === index} size="sm" variant="outline" className="mt-3 border-meritum-teal text-meritum-teal hover:bg-meritum-teal hover:text-white font-body text-xs" data-testid={`resend-invite-btn-${index}`}>
                                        {sendingInvite === index ? <><Loader2 size={12} className="mr-1 animate-spin" />Resending...</> : <><Send size={12} className="mr-1" />Resend Invite</>}
                                      </Button>
                                    )}
                                    {/* Show "waiting period" hint for recent pending invites */}
                                    {skill.reference.email && getSkillVerificationStatus(skill.name) === 'pending' && !canResendInvite(index) && (() => {
                                      const inv = getLatestInvitation(index);
                                      if (!inv?.sent_at) return null;
                                      const daysSince = Math.floor((Date.now() - new Date(inv.sent_at).getTime()) / (1000 * 60 * 60 * 24));
                                      const daysLeft = 7 - daysSince;
                                      return (
                                        <p className="mt-2 font-body text-[11px] text-slate-400" data-testid={`resend-wait-${index}`}>
                                          You can resend in {daysLeft} day{daysLeft !== 1 ? 's' : ''}.
                                        </p>
                                      );
                                    })()}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : <p className="font-body text-slate-400 text-sm">No skills added yet. Click Edit then Add Skill to get started.</p>}
                  </div>
                )}

                {/* Portfolio Tab */}
                {activeTab === 'portfolio' && (
                  <div className="space-y-5" data-testid="tab-content-portfolio">
                    <h3 className="font-heading text-lg font-semibold text-meritum-navy flex items-center gap-2"><FolderOpen size={20} className="text-violet-600" />Portfolio Links</h3>
                    <p className="font-body text-xs text-slate-500">Link your portfolios to showcase your work.</p>
                    <div className="space-y-4">
                      {portfolioPlatforms.map((platform) => (
                        <div key={platform.id} className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-violet-50 rounded-lg flex items-center justify-center flex-shrink-0"><platform.icon size={18} className="text-violet-600" /></div>
                          <div className="flex-1">
                            <Label className="font-body text-sm font-medium text-meritum-navy">{platform.name}</Label>
                            {isEditing ? <Input value={profile.portfolio_links[platform.id] || ''} onChange={(e) => handlePortfolioChange(platform.id, e.target.value)} placeholder={platform.placeholder} className="mt-1" data-testid={`${platform.id}-input`} />
                            : <p className="font-body text-sm mt-1">{profile.portfolio_links[platform.id] ? <a href={ensureAbsoluteUrl(profile.portfolio_links[platform.id])} target="_blank" rel="noopener noreferrer" className="text-meritum-teal hover:underline inline-flex items-center gap-1">{profile.portfolio_links[platform.id]}<ExternalLink size={12} /></a> : <span className="text-slate-400">Not linked</span>}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Messages Tab */}
                {activeTab === 'messages' && (
                  <div className="space-y-5" data-testid="tab-content-messages">
                    {isPro ? (
                      <MessagesPanel currentUserId={user.id} />
                    ) : (
                      <div className="text-center py-16" data-testid="messages-locked">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-meritum-gold/10 mb-4">
                          <Award size={28} className="text-meritum-gold" />
                        </div>
                        <h3 className="font-heading text-lg font-semibold text-meritum-navy">Messaging is a Professional feature</h3>
                        <p className="font-body text-sm text-slate-500 mt-2 max-w-sm mx-auto">Upgrade to chat directly with organizations who view your profile.</p>
                        <Button onClick={() => navigate('/pricing')} className="mt-4 bg-meritum-gold text-meritum-navy hover:bg-meritum-gold/90 font-body font-bold" data-testid="messages-upgrade-btn">Upgrade to Professional</Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Preview Tab */}
                {activeTab === 'preview' && (
                  <div className="space-y-6" data-testid="tab-content-preview">
                    <h3 className="font-heading text-lg font-semibold text-meritum-navy flex items-center gap-2"><Eye size={20} className="text-cyan-600" />Profile Preview</h3>
                    <p className="font-body text-xs text-slate-500">This is how organisations see your profile.</p>
                    <div className="border border-slate-200 rounded-xl p-6 bg-slate-50">
                      {/* Preview Header */}
                      <div className="flex items-start gap-5 mb-6">
                        <div className="w-20 h-20 rounded-full bg-meritum-teal/10 flex items-center justify-center overflow-hidden border-3 border-meritum-gold/30 flex-shrink-0">
                          {profile.profile_picture ? <img src={profile.profile_picture} alt="" className="w-full h-full object-cover" /> : <User size={32} className="text-meritum-teal" />}
                        </div>
                        <div>
                          <h2 className="font-heading text-2xl font-bold text-meritum-navy">{user.name}</h2>
                          {profile.headline && <p className="font-body text-meritum-teal font-medium mt-1">{profile.headline}</p>}
                          {(profile.city || profile.country) && <p className="font-body text-sm text-slate-500 mt-1">{profile.city}{profile.city && profile.country ? ', ' : ''}{profile.country}</p>}
                          {profile.gender && <p className="font-body text-sm text-slate-400">{profile.gender}</p>}
                          <p className="font-body text-sm text-slate-500 mt-1">{user.email}</p>
                        </div>
                      </div>
                      {profile.bio && <div className="mb-6"><h4 className="font-heading text-base font-semibold text-meritum-navy mb-2">About</h4><p className="font-body text-sm text-slate-600 whitespace-pre-wrap">{profile.bio}</p></div>}
                      {profile.education.length > 0 && (
                        <div className="mb-6">
                          <h4 className="font-heading text-base font-semibold text-meritum-navy mb-2">Education / Training</h4>
                          {sortByLatest(profile.education, 'end_year', 'start_year').map((e, i) => (
                            <div key={i} className="mb-2 p-3 bg-white rounded-lg border border-slate-100">
                              <p className="font-heading text-sm font-semibold text-meritum-navy">{e.institution}</p>
                              {e.qualification && <p className="font-body text-sm text-slate-600">{e.qualification}{e.field_of_study ? ` in ${e.field_of_study}` : ''}</p>}
                              {e.start_year && <p className="font-body text-xs text-slate-400">{e.start_year}{e.end_year ? ` - ${e.end_year}` : ''}</p>}
                            </div>
                          ))}
                        </div>
                      )}
                      {profile.experience.length > 0 && (
                        <div className="mb-6">
                          <h4 className="font-heading text-base font-semibold text-meritum-navy mb-2">Experience</h4>
                          {sortByLatest(profile.experience, 'end_date', 'start_date').map((exp, i) => (
                            <div key={i} className="mb-2 p-3 bg-white rounded-lg border border-slate-100">
                              <p className="font-heading text-sm font-semibold text-meritum-navy">{exp.role}</p>
                              {exp.company && <p className="font-body text-sm text-meritum-teal">{exp.company}{exp.location ? ` - ${exp.location}` : ''}</p>}
                              {exp.start_date && <p className="font-body text-xs text-slate-400">{exp.start_date}{exp.is_current ? ' - Present' : (exp.end_date ? ` - ${exp.end_date}` : '')}</p>}
                              {exp.responsibilities && <p className="font-body text-xs text-slate-600 mt-1">{exp.responsibilities}</p>}
                            </div>
                          ))}
                        </div>
                      )}
                      {profile.skills.length > 0 && (
                        <div className="mb-6">
                          <h4 className="font-heading text-base font-semibold text-meritum-navy mb-2">Skills & References</h4>
                          {profile.skills.map((s, i) => {
                            const status = getSkillVerificationStatus(s.name);
                            return (
                              <div key={i} className="mb-2 p-3 bg-white rounded-lg border border-slate-100">
                                <div className="flex items-center gap-2">
                                  <p className="font-heading text-sm font-semibold text-meritum-navy">{s.name}</p>
                                  {status === 'confirmed' && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full font-body"><ShieldCheck size={10} />Verified</span>}
                                  {status === 'pending' && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full font-body"><Clock size={10} />Pending</span>}
                                  {status === 'declined' && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full font-body"><ShieldX size={10} />Declined</span>}
                                </div>
                                {s.reference?.name && <p className="font-body text-xs text-slate-500">Ref: {s.reference.name} {s.reference.surname} - {s.reference.title} at {s.reference.organisation}</p>}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {/* Verification Log */}
                      {verificationLogs.length > 0 && (
                        <div className="mb-6">
                          <h4 className="font-heading text-base font-semibold text-meritum-navy mb-2 flex items-center gap-2"><History size={16} className="text-meritum-teal" />Verification Activity</h4>
                          <div className="space-y-2 max-h-64 overflow-y-auto">
                            {verificationLogs.slice(0, 10).map((log, i) => (
                              <div key={i} className="p-2 bg-white rounded-lg border border-slate-100 flex items-center gap-3">
                                {log.action === 'invitation_sent' && <Send size={14} className="text-blue-500 flex-shrink-0" />}
                                {log.action === 'reference_confirmed' && <CheckCircle2 size={14} className="text-emerald-500 flex-shrink-0" />}
                                {log.action === 'reference_declined' && <XCircle size={14} className="text-red-500 flex-shrink-0" />}
                                <div className="flex-1 min-w-0">
                                  <p className="font-body text-xs text-meritum-navy font-medium truncate">
                                    {log.action === 'invitation_sent' && `Invite sent to ${log.details?.referee_name} for "${log.details?.skill_name}"`}
                                    {log.action === 'reference_confirmed' && `${log.details?.referee_name} confirmed "${log.details?.skill_name}"`}
                                    {log.action === 'reference_declined' && `${log.details?.referee_name} declined "${log.details?.skill_name}"`}
                                  </p>
                                </div>
                                <span className="font-body text-[10px] text-slate-400 flex-shrink-0">{new Date(log.timestamp).toLocaleDateString()}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {Object.entries(profile.portfolio_links).filter(([,v]) => v).length > 0 && (
                        <div>
                          <h4 className="font-heading text-base font-semibold text-meritum-navy mb-2">Portfolio</h4>
                          {Object.entries(profile.portfolio_links).filter(([,v]) => v).map(([k, v]) => {
                            const p = portfolioPlatforms.find(pl => pl.id === k);
                            return <div key={k} className="mb-1"><a href={ensureAbsoluteUrl(v)} target="_blank" rel="noopener noreferrer" className="font-body text-sm text-meritum-teal hover:underline inline-flex items-center gap-1"><ExternalLink size={12} />{p?.name || k}: {v}</a></div>;
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Bottom Save Bar (visible during edit) */}
      {isEditing && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] py-3 px-4" data-testid="sticky-save-bar">
          <div className="container mx-auto max-w-7xl flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              {autosaveStatus === 'saving' && (
                <><Loader2 size={14} className="text-meritum-teal animate-spin flex-shrink-0" /><span className="font-body text-xs sm:text-sm text-slate-500 truncate">Auto-saving...</span></>
              )}
              {autosaveStatus === 'saved' && (
                <><CheckCircle2 size={14} className="text-emerald-600 flex-shrink-0" /><span className="font-body text-xs sm:text-sm text-emerald-600 truncate">Auto-saved</span></>
              )}
              {!autosaveStatus && (
                <span className="font-body text-xs sm:text-sm text-slate-400 truncate">Changes save automatically</span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button onClick={() => setIsEditing(false)} variant="outline" size="sm" className="border-slate-300 text-slate-600 hover:bg-slate-50 font-body" data-testid="bottom-cancel-btn">Done</Button>
              <Button onClick={handleSaveProfile} disabled={isSaving} size="sm" className="bg-meritum-gold text-meritum-navy hover:bg-meritum-gold/90 font-body font-bold" data-testid="bottom-save-btn">
                <Save size={14} className="mr-1" />{isSaving ? 'Saving...' : 'Save Now'}
              </Button>
            </div>
          </div>
        </div>
      )}
      {isEditing && <div className="h-20" />}
    </div>
  );
}
